// Политика почты установки (022: research, Р1, Р2; data-model.md, «Политика почты»; ADR-0040):
// корпоративные домены и настройка «Внешний доступ к протоколам». По ней адрес — сотрудник
// компании, внешний (домен разрешён настройкой) или вход выключен. Признак у пользователя не
// хранится: сервер и экран считают его этой функцией по текущим настройкам (FR-002).
import { z } from '../kernel/zod.ts';

/** Значение настройки «Внешний доступ к протоколам» (Г1): по умолчанию — off. */
export const externalAccessModes = ['off', 'listed', 'any'] as const;
export type ExternalAccessMode = (typeof externalAccessModes)[number];
export const ExternalAccessModeSchema = z.enum(externalAccessModes);

/** Подписи значений в «Настройках → Компания» (spec, сценарий 1.1). */
export const externalAccessLabels = {
  off: 'Выключен',
  listed: 'Только домены из списка',
  any: 'Любые адреса',
} as const satisfies Record<ExternalAccessMode, string>;

/** Корпоративные домены и внешний доступ — всё, что решает, кем считается адрес. */
export interface EmailPolicy {
  readonly corporate: readonly string[];
  readonly externalAccess: ExternalAccessMode;
  readonly externalDomains: readonly string[];
}

/** Кем считается адрес: сотрудник компании, внешний или вход выключен. */
export const emailStandings = ['corporate', 'external', 'blocked'] as const;
export type EmailStanding = (typeof emailStandings)[number];
export const EmailStandingSchema = z.enum(emailStandings);

/** Метка внешнего пользователя: «Внешний» или «Внешний доступ выключен»; null — сотрудник. */
export const externalStates = ['active', 'disabled'] as const;
export type ExternalState = (typeof externalStates)[number];
export const ExternalStateSchema = z.enum(externalStates);

/** Домен адреса: после последней «@», без пробелов по краям, в нижнем регистре. */
export function emailDomain(email: string): string {
  const address = email.trim();
  return address.slice(address.lastIndexOf('@') + 1).toLowerCase();
}

/**
 * Кем считается адрес (data-model.md, таблица «Политика почты»): домен в корпоративных —
 * сотрудник, даже если он и во внешнем списке (EXT-08); иначе «Любые адреса» или домен
 * из списка при «Только домены из списка» — внешний; иначе вход выключен. Совпадение домена —
 * точное, поддомены не входят.
 */
export function emailStanding(policy: EmailPolicy, email: string): EmailStanding {
  const domain = emailDomain(email);
  if (policy.corporate.some((item) => item.toLowerCase() === domain)) return 'corporate';
  if (policy.externalAccess === 'any') return 'external';
  if (
    policy.externalAccess === 'listed' &&
    policy.externalDomains.some((item) => item.toLowerCase() === domain)
  ) {
    return 'external';
  }
  return 'blocked';
}

/**
 * Домены, на которые можно пригласить, — для текста ошибки «Разрешены: …» (REC-004):
 * корпоративные, затем внешние из списка (только при «Только домены из списка»), без повторов.
 */
export function allowedDomains(policy: EmailPolicy): string[] {
  const external = policy.externalAccess === 'listed' ? policy.externalDomains : [];
  return [...new Set([...policy.corporate, ...external])];
}

/** Метка пользователя по признаку адреса: сотрудник — null. */
export function externalStateOf(standing: EmailStanding): ExternalState | null {
  if (standing === 'corporate') return null;
  return standing === 'external' ? 'active' : 'disabled';
}
